import { parseISO } from "date-fns";
import { logError } from "@entrilia/shared/logging";
import type { StateAction } from "@entrilia/shared/types";
import { groupByToMap } from "@entrilia/shared/utilities/arrayHelper";
import { formatDateOnly } from "@entrilia/shared/utilities/dateUtils";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { convertToNumber } from "@entrilia/shared/utilities/numberHelper";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { ObjectFieldValues } from "../../../../../../api/types/objectTypes";
import type {
  ChangeSetUpdate,
  UpdateWorkflowActivityChangeSetsRequest,
  WorkflowActivityChangeSet,
} from "../../../../../../api/types/workflowTypes";

export interface TransactionEditState {
  investmentCompany: ObjectFieldValues | null;
  description: string;
  postingDate: Date | null;
  isMultiEntity: boolean;
  entityGroup: ObjectFieldValues | null;
  entity: ObjectFieldValues | null;
  fundingEntity: ObjectFieldValues | null;
  relatedEntities: TransactionRelatedEntity[];
  lines: TransactionLine[];
  enitityGroupsMap: Map<string, string[]>; // EntityGroupCode -> array of EntityNo
  validationResults?: ValidationResults;
}

export interface TransactionRelatedEntity {
  isNew: boolean;
  changeSetId: string;
  isIncluded: boolean;
  entity: ObjectFieldValues | null;
  fundingEntity: ObjectFieldValues | null;
}

export interface TransactionLine {
  isNew: boolean;
  changeSetId: string;
  transactionCode: ObjectFieldValues | null;
  positionType: ObjectFieldValues | null;
  positionNo: string;
  positionDescription: string;
  account: ObjectFieldValues | null;
  description: string;
  shares: number | undefined;
  amount: number;
  currencyCode: string | null;
  investorAllocationRule: ObjectFieldValues | null;
  entityAllocationRule: ObjectFieldValues | null;
}

interface ValidationResults {
  invalidFields: string[];
  relatedEntitiesInvalidFields: Record<string, string[]>; // changeSetId -> array of invalid field names
  linesInvalidFields: Record<string, string[]>; // changeSetId -> array of invalid field names
  isValid: boolean;
}

export interface TransactionRelatedEntities {
  portfolioCompanies: ObjectFieldValues[];
  entities: ObjectFieldValues[];
  entityGroups: ObjectFieldValues[];
  entityGroupAssignments: ObjectFieldValues[];
}

export interface TransactionLineRelatedEntities {
  transactionCodes: ObjectFieldValues[];
  positionTypes: ObjectFieldValues[];
  accounts: ObjectFieldValues[];
  allocationRules: ObjectFieldValues[];
}

const getDateFromValue = (value: unknown): Date | null => {
  const dateStr = stringifyValue(value);
  return dateStr ? parseISO(dateStr) : null;
};

export const getInitialTransactionEditState = (activityChangeSet: WorkflowActivityChangeSet): TransactionEditState => {
  const state: TransactionEditState = {
    investmentCompany: null,
    description: "",
    postingDate: null,
    isMultiEntity: false,
    entityGroup: null,
    entity: null,
    fundingEntity: null,
    relatedEntities: [],
    lines: [],
    enitityGroupsMap: new Map(),
  };

  for (const field of activityChangeSet.fields) {
    if (field.name === "Description") {
      state.description = stringifyValue(field.newValue);
    } else if (field.name === "PostingDate") {
      state.postingDate = getDateFromValue(field.newValue);
    } else if (field.name === "IsMultiEntity") {
      state.isMultiEntity = typeof field.newValue === "boolean" && field.newValue;
    }
  }

  return state;
};

const mapStateToRelatedEntitiesChangeSetUpdates = (state: TransactionEditState) => {
  const result: Record<string, ChangeSetUpdate> = {};

  if (!state.isMultiEntity) {
    return result;
  }

  for (const relatedEntity of state.relatedEntities) {
    const entityNo = stringifyValue(relatedEntity.entity?.fieldValues["No"]);

    result[relatedEntity.changeSetId] = {
      fieldUpdates: [
        { name: "Include", valueUserInput: relatedEntity.isIncluded },
        { name: "EntityNo", valueUserInput: entityNo || null },
        { name: "EntityName", valueUserInput: relatedEntity.entity?.fieldValues["Name"] || null },
        { name: "FundingEntityNo", valueUserInput: relatedEntity.fundingEntity?.fieldValues["No"] || null },
        { name: "FundAccountingLink", valueUserInput: null },
      ],
      newChangeSetMetadata: relatedEntity.isNew
        ? {
            relatedChangeSetsName: "Entities",
            name: "Entity",
            action: "None",
            objectType: "InvestmentAcquisitionTransactionEntity",
          }
        : undefined,
    };
  }

  return result;
};

const mapStateToTransactionLinesChangeSetUpdates = (state: TransactionEditState) => {
  const result: Record<string, ChangeSetUpdate> = {};

  for (const line of state.lines) {
    result[line.changeSetId] = {
      fieldUpdates: [
        { name: "TransactionCode", valueUserInput: line.transactionCode?.fieldValues["Code"] || null },
        { name: "PositionTypeCode", valueUserInput: line.positionType?.fieldValues["Code"] || null },
        { name: "PositionNo", valueUserInput: line.positionNo },
        { name: "PositionDescription", valueUserInput: line.positionDescription },
        { name: "AccountType", valueUserInput: "G/L Account" },
        { name: "AccountNo", valueUserInput: line.account?.fieldValues["No."] || null },
        { name: "Description", valueUserInput: line.description },
        { name: "Shares", valueUserInput: line.shares ?? null },
        { name: "Amount", valueUserInput: line.amount },
        { name: "CurrencyCode", valueUserInput: line.currencyCode || null },
        {
          name: "EntityAllocationRule",
          valueUserInput: state.isMultiEntity ? line.entityAllocationRule?.fieldValues["Code"] || null : null,
        },
        {
          name: "InvestorAllocationRule",
          valueUserInput: state.isMultiEntity ? null : line.investorAllocationRule?.fieldValues["Code"] || null,
        },
      ],
      newChangeSetMetadata: line.isNew
        ? {
            relatedChangeSetsName: "Lines",
            name: "Transaction Line",
            action: "None",
            objectType: "InvestmentAcquisitionTransactionLine",
          }
        : undefined,
    };
  }

  return result;
};

export const mapStateToUpdateWorkflowActivityChangeSetsRequest = (
  state: TransactionEditState,
  activityChangeSet: WorkflowActivityChangeSet
): UpdateWorkflowActivityChangeSetsRequest => {
  const relatedEntitiesChangeSetUpdates = mapStateToRelatedEntitiesChangeSetUpdates(state);
  const transactionLinesChangeSetUpdates = mapStateToTransactionLinesChangeSetUpdates(state);

  return {
    changeSetUpdates: {
      ...relatedEntitiesChangeSetUpdates,
      ...transactionLinesChangeSetUpdates,
      [activityChangeSet.id]: {
        fieldUpdates: [
          { name: "Description", valueUserInput: state.description },
          { name: "InvestmentNo", valueUserInput: state.investmentCompany?.fieldValues["No"] || null },
          { name: "PostingDate", valueUserInput: state.postingDate ? formatDateOnly(state.postingDate) : null },
          { name: "IsMultiEntity", valueUserInput: state.isMultiEntity },
          { name: "EntityGroupCode", valueUserInput: state.entityGroup?.fieldValues["Code"] || null },
          { name: "EntityNo", valueUserInput: state.entity?.fieldValues["No"] || null },
          { name: "FundingEntityNo", valueUserInput: state.fundingEntity?.fieldValues["No"] || null },
        ],
      },
    },
  };
};

export const validateTransactionEditState = (state: TransactionEditState): ValidationResults => {
  const result: ValidationResults = {
    invalidFields: [],
    relatedEntitiesInvalidFields: {},
    linesInvalidFields: {},
    isValid: true,
  };

  if (!state.investmentCompany) {
    result.invalidFields.push("investmentCompany");
  }
  if (!state.postingDate) {
    result.invalidFields.push("postingDate");
  }
  if (state.isMultiEntity && !state.entityGroup) {
    result.invalidFields.push("entityGroup");
  }
  if (!state.isMultiEntity && !state.entity) {
    result.invalidFields.push("entity");
  }
  if (!state.isMultiEntity && !state.fundingEntity) {
    result.invalidFields.push("fundingEntity");
  }

  for (const relatedEntity of state.relatedEntities) {
    const relatedEntityInvalidFields: string[] = [];

    if (!relatedEntity.entity) {
      relatedEntityInvalidFields.push("entity");
    }
    if (!relatedEntity.fundingEntity) {
      relatedEntityInvalidFields.push("fundingEntity");
    }

    if (relatedEntityInvalidFields.length > 0) {
      result.relatedEntitiesInvalidFields[relatedEntity.changeSetId] = relatedEntityInvalidFields;
    }
  }

  for (const line of state.lines) {
    const lineInvalidFields: string[] = [];

    if (!line.transactionCode) {
      lineInvalidFields.push("transactionCode");
    }
    if (!line.positionType) {
      lineInvalidFields.push("positionType");
    }
    if (!line.positionNo) {
      lineInvalidFields.push("positionNo");
    }
    if (!line.account) {
      lineInvalidFields.push("account");
    }
    if (line.amount <= 0) {
      lineInvalidFields.push("amount");
    }
    if (line.shares !== undefined && line.shares < 0) {
      lineInvalidFields.push("shares");
    }
    if (!line.currencyCode) {
      lineInvalidFields.push("currencyCode");
    }
    if (state.isMultiEntity && !line.entityAllocationRule) {
      lineInvalidFields.push("entityAllocationRule");
    }
    if (!state.isMultiEntity && !line.investorAllocationRule) {
      lineInvalidFields.push("investorAllocationRule");
    }

    if (lineInvalidFields.length > 0) {
      result.linesInvalidFields[line.changeSetId] = lineInvalidFields;
    }
  }

  if (
    result.invalidFields.length > 0 ||
    Object.keys(result.relatedEntitiesInvalidFields).length > 0 ||
    Object.keys(result.linesInvalidFields).length > 0
  ) {
    result.isValid = false;
  }

  return result;
};

// State Actions

export const fillTransactionRelatedEntitiesAction =
  (
    activityChangeSet: WorkflowActivityChangeSet,
    allTransactionRelatedEntities: TransactionRelatedEntities
  ): StateAction<TransactionEditState> =>
  (state) => {
    const { portfolioCompanies, entities, entityGroups, entityGroupAssignments } = allTransactionRelatedEntities;

    const investmentNo = activityChangeSet.fields.find((f) => f.name === "InvestmentNo")?.newValue;
    const investmentCompany = investmentNo
      ? (portfolioCompanies.find((c) => c.fieldValues["No"] === investmentNo) ?? null)
      : null;

    const entityGroupCode = activityChangeSet.fields.find((f) => f.name === "EntityGroupCode")?.newValue;
    const entityGroup = entityGroupCode
      ? (entityGroups.find((c) => c.fieldValues["Code"] === entityGroupCode) ?? null)
      : null;

    const entityNo = activityChangeSet.fields.find((f) => f.name === "EntityNo")?.newValue;
    const fundingEntityNo = activityChangeSet.fields.find((f) => f.name === "FundingEntityNo")?.newValue;

    const entity = entityNo ? (entities.find((c) => c.fieldValues["No"] === entityNo) ?? null) : null;
    const fundingEntity = fundingEntityNo
      ? (entities.find((c) => c.fieldValues["No"] === fundingEntityNo) ?? null)
      : null;

    const relatedEntities = mapRelatedEntitiesFromChangeSet(activityChangeSet, entities);

    const enitityGroupsMap = groupByToMap(
      entityGroupAssignments
        .map((assignmentObj) => ({
          entityGroupCode: stringifyValue(assignmentObj.fieldValues["Entity Group Code"]),
          entityNo: stringifyValue(assignmentObj.fieldValues["Entity No."]),
        }))
        .filter((x) => x.entityGroupCode && x.entityNo),
      (x) => x.entityGroupCode,
      (x) => x.entityNo
    );

    return {
      ...state,
      investmentCompany,
      entityGroup,
      entity,
      fundingEntity,
      relatedEntities,
      enitityGroupsMap,
    };
  };

const getRelatedEntityChangeSets = (activityChangeSet: WorkflowActivityChangeSet) =>
  activityChangeSet.relatedChangeSets?.find((cs) => cs.name === "Entities")?.changeSets ?? [];

const getTransactionLinesChangeSets = (activityChangeSet: WorkflowActivityChangeSet) =>
  activityChangeSet.relatedChangeSets?.find((cs) => cs.name === "Lines")?.changeSets ?? [];

const mapRelatedEntitiesFromChangeSet = (
  activityChangeSet: WorkflowActivityChangeSet,
  entities: ObjectFieldValues[]
): TransactionRelatedEntity[] => {
  return getRelatedEntityChangeSets(activityChangeSet).map((changeSet) => {
    const relatedEntity: TransactionRelatedEntity = {
      isNew: false,
      changeSetId: changeSet.id,
      isIncluded: false,
      entity: {} as ObjectFieldValues,
      fundingEntity: null,
    };

    for (const field of changeSet.fields) {
      if (field.name === "Include") {
        relatedEntity.isIncluded = typeof field.newValue === "boolean" && field.newValue;
      } else if (field.name === "EntityNo") {
        const entityNo = field.newValue;
        const entity = entityNo ? (entities.find((c) => c.fieldValues["No"] === entityNo) ?? null) : null;
        relatedEntity.entity = entity;
      } else if (field.name === "FundingEntityNo") {
        const fundingEntityNo = field.newValue;
        const fundingEntity = fundingEntityNo
          ? (entities.find((c) => c.fieldValues["No"] === fundingEntityNo) ?? null)
          : null;
        relatedEntity.fundingEntity = fundingEntity;
      }
    }

    return relatedEntity;
  });
};

export const fillTransactionLinesAction =
  (
    activityChangeSet: WorkflowActivityChangeSet,
    allLineRelatedEntities: TransactionLineRelatedEntities
  ): StateAction<TransactionEditState> =>
  (state) => {
    const { transactionCodes, positionTypes, accounts, allocationRules } = allLineRelatedEntities;

    const lines = getTransactionLinesChangeSets(activityChangeSet).map((changeSet) => {
      const line: TransactionLine = {
        isNew: false,
        changeSetId: changeSet.id,
        transactionCode: null,
        positionType: null,
        positionNo: "",
        positionDescription: "",
        account: null,
        description: "",
        shares: undefined,
        amount: 0,
        currencyCode: null,
        investorAllocationRule: null,
        entityAllocationRule: null,
      };

      for (const field of changeSet.fields) {
        switch (field.name) {
          case "TransactionCode":
            line.transactionCode = transactionCodes.find((tc) => tc.fieldValues["Code"] === field.newValue) ?? null;
            break;
          case "PositionTypeCode":
            line.positionType = positionTypes.find((pt) => pt.fieldValues["Code"] === field.newValue) ?? null;
            break;
          case "PositionNo":
            line.positionNo = stringifyValue(field.newValue);
            break;
          case "PositionDescription":
            line.positionDescription = stringifyValue(field.newValue);
            break;
          case "AccountType":
            // Ignored field - we use G/L Account only
            break;
          case "AccountNo":
            line.account = accounts.find((a) => a.fieldValues["No."] === field.newValue) ?? null;
            break;
          case "Description":
            line.description = stringifyValue(field.newValue);
            break;
          case "Shares":
            line.shares = convertToNumber(field.newValue);
            break;
          case "Amount":
            line.amount = convertToNumber(field.newValue) ?? 0;
            break;
          case "CurrencyCode":
            line.currencyCode = stringifyValue(field.newValue) || null;
            break;
          case "InvestorAllocationRule":
            line.investorAllocationRule =
              allocationRules.find(
                (ar) => ar.fieldValues["Level"] === "Investor" && ar.fieldValues["Code"] === field.newValue
              ) ?? null;
            break;
          case "EntityAllocationRule":
            line.entityAllocationRule =
              allocationRules.find(
                (ar) => ar.fieldValues["Level"] === "Entity" && ar.fieldValues["Code"] === field.newValue
              ) ?? null;
            break;
          default:
            logError(
              `Unexpected field name: ${field.name} in change set ${activityChangeSet.id}`,
              "[TransactionEditState] fillTransactionLinesAction"
            );
            break;
        }
      }

      return line;
    });

    return { ...state, lines };
  };

export const updateIsMultiEntityAction =
  (isMultiEntity: boolean): StateAction<TransactionEditState> =>
  (state) => {
    const newLines = state.lines.map((line) => ({ ...line, investorAllocationRule: null, entityAllocationRule: null }));

    return {
      ...state,
      isMultiEntity,
      entityGroup: null,
      entity: null,
      fundingEntity: null,
      relatedEntities: [],
      lines: newLines,
    };
  };

export const updateEntityGroupAction =
  (entityGroup: ObjectFieldValues | null, allEntities: ObjectFieldValues[]): StateAction<TransactionEditState> =>
  (state) => {
    if (!entityGroup) {
      return { ...state, entityGroup: null, relatedEntities: [] };
    }

    const entityGroupCode = stringifyValue(entityGroup.fieldValues["Code"]);
    if (!entityGroupCode) {
      return { ...state, entityGroup, relatedEntities: [] };
    }

    const relatedEntityNosForGroup = state.enitityGroupsMap.get(entityGroupCode);
    if (!relatedEntityNosForGroup?.length) {
      return { ...state, entityGroup, relatedEntities: [] };
    }

    const relatedEntities = relatedEntityNosForGroup.map((entityNo): TransactionRelatedEntity => {
      const entity = allEntities.find((e) => e.fieldValues["No"] === entityNo) ?? null;
      return {
        isNew: true,
        changeSetId: generateGuid(),
        isIncluded: true,
        entity,
        fundingEntity: entity ? { ...entity } : null,
      };
    });

    return { ...state, entityGroup, relatedEntities };
  };

export const addTransactionLineAction = (): StateAction<TransactionEditState> => (state) => {
  const currencyCode =
    state.lines[0]?.currencyCode ||
    stringifyValue(state.entity?.fieldValues["CurrencyCode"]) ||
    stringifyValue(state.relatedEntities[0]?.entity?.fieldValues["CurrencyCode"]) ||
    null;

  const newLine: TransactionLine = {
    isNew: true,
    changeSetId: generateGuid(),
    transactionCode: null,
    positionType: null,
    positionNo: "",
    positionDescription: "",
    account: null,
    description: "",
    shares: undefined,
    amount: 0,
    currencyCode,
    investorAllocationRule: null,
    entityAllocationRule: null,
  };

  return { ...state, lines: [...state.lines, newLine] };
};

export const removeTransactionLineAction =
  (changeSetId: string): StateAction<TransactionEditState> =>
  (state) => {
    const newLines = state.lines.filter((line) => line.changeSetId !== changeSetId);
    return { ...state, lines: newLines };
  };
